<script setup lang="ts">
import {
  computed,
  type CSSProperties,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";

export type BcSliderProps = {
  min?: number;
  max?: number;
  step?: number;
  modelValue?: number;
  markers?: number[];
};

export type BcSliderEmits = {
  (event: "update", value: number): void;
};

const {
  min = 0.1,
  max = 10,
  step = 0.1,
  modelValue,
  markers = [],
} = defineProps<BcSliderProps>();

const emit = defineEmits<BcSliderEmits>();

const trackItemsRef = useTemplateRef("trackItemsRef");
const initialValue = ref<number>(1);
const sliderValue = ref(1);
const thumbPosition = ref(1);
const isDragging = ref(false);
const startX = ref(0);
const startWidth = ref(0);

onMounted(() => {
  initialValue.value = modelValue ?? min;
  sliderValue.value = initialValue.value;
  thumbPosition.value = getPositionInSlider(modelValue ?? initialValue.value);
});

onUnmounted(() => {
  removeEventListenersOnDocument();
});

const pak = ref("bc-controls-d7");

const thumbStyle = computed(() => {
  return {
    "--thumbPosition": `${thumbPosition.value}%`,
  };
});

function getMarkerStyle(marker: number): CSSProperties {
  const markerPos = getPositionInSlider(marker);
  const isVisible = 0 <= markerPos && markerPos <= 100;
  return {
    "--markerPosition": `${markerPos}%`,
    visibility: isVisible ? "initial" : "hidden",
  };
}

function onMarkerClicked(marker: number): void {
  updateSliderValue(marker);
  updateThumbBySliderValue(sliderValue.value);
}

//  v-model pattern
function emitUpdateValue(): void {
  emit("update", sliderValue.value);
}

function getPositionInSlider(value: number): number {
  return ((value - min) / (max - min)) * 100;
}

function updateSliderValue(value: number): void {
  sliderValue.value = value;
  emitUpdateValue();
}

function onMinusClicked(): void {
  const value = Math.max(sliderValue.value - step, min);

  updateSliderAfterStepperClicked(value);
}

function onPlusClicked(): void {
  const value = Math.min(sliderValue.value + step, max);

  updateSliderAfterStepperClicked(value);
}

function updateSliderAfterStepperClicked(value: number): void {
  updateSliderValue(value);
  updateThumbBySliderValue(sliderValue.value);
}

function updateThumbBySliderValue(value: number): void {
  const thumbPosition = getPositionInSlider(value);
  setThumbPosition(thumbPosition);
}

function onPointerDown(event: PointerEvent): void {
  addEventListenersOnDocument();
  moveThumbToPosition(event);
}

function addEventListenersOnDocument(): void {
  document.addEventListener("pointermove", dragThumb);
  document.addEventListener("pointerup", endDrag);
  document.addEventListener("pointercancel", endDrag);
}

function removeEventListenersOnDocument(): void {
  document.removeEventListener("pointermove", dragThumb);
  document.removeEventListener("pointerup", endDrag);
  document.removeEventListener("pointercancel", endDrag);
}

function calculateNewSliderValue(value: number): number {
  const decimalValue = (value / 100) * (max - min);
  const roundedValue = Math.round(decimalValue / step) * step;
  const finalValue = roundedValue + min;
  return finalValue;
}

function setThumbPosition(value: number): void {
  thumbPosition.value = value;
}

function moveThumbToPosition(event: PointerEvent): void {
  event.preventDefault();
  isDragging.value = true;
  if (!trackItemsRef.value) return;

  const track = trackItemsRef.value.getBoundingClientRect();
  const trackWidth = track.width;
  const clickX = event.clientX - track.left;
  const newPos = (Math.max(0, Math.min(trackWidth, clickX)) / trackWidth) * 100;
  const newSliderValue = calculateNewSliderValue(newPos);

  updateSliderValue(newSliderValue);
  setThumbPosition(newPos);
  setStartValuesForDragging(event.clientX, thumbPosition.value);
  startDrag(event);
}

function dragThumb(event: PointerEvent): void {
  if (isDragging.value) {
    event.preventDefault();
    if (!trackItemsRef.value) return;

    const track = trackItemsRef.value.getBoundingClientRect();
    const trackWidth = track.width;
    const clientX = event.clientX;
    const diffX = clientX - startX.value;
    const circleX = (startWidth.value / 100) * trackWidth;
    const newWidth =
      (Math.max(0, Math.min(circleX + diffX, trackWidth)) / trackWidth) * 100;
    const newSliderValue = calculateNewSliderValue(newWidth);

    updateSliderValue(newSliderValue);
    setThumbPosition(newWidth);
  }
}

function setStartValuesForDragging(clientX: number, width: number): void {
  startX.value = clientX;
  startWidth.value = width;
}

function startDrag(event: PointerEvent): void {
  event.preventDefault();
  isDragging.value = true;
  setStartValuesForDragging(event.clientX, thumbPosition.value);
}

function endDrag(): void {
  isDragging.value = false;
  setStartValuesForDragging(0, 0);
  removeEventListenersOnDocument();
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
watch([() => modelValue, () => max, () => min], (newValues) => {
  const propsValue = newValues[0];
  sliderValue.value = propsValue ?? initialValue.value;
  updateThumbBySliderValue(sliderValue.value);
});
</script>

<template>
  <div class="bissantzControls sliderControl" v-bind:data-pak="pak">
    <div class="minus button" v-on:click="onMinusClicked">
      <div class="horizontalLine" />
    </div>

    <div class="track" v-on:pointerdown="onPointerDown">
      <div class="horizontalLine" />
      <div class="trackItems" ref="trackItemsRef">
        <div
          v-for="(marker, index) in markers"
          v-bind:key="index"
          class="marker-wrapper"
          v-on:pointerdown.stop="onMarkerClicked(marker)"
          v-bind:style="getMarkerStyle(marker)"
        >
          <div class="marker" />
        </div>
        <div class="thumb" v-bind:style="thumbStyle"></div>
      </div>
    </div>

    <div class="plus button" v-on:click="onPlusClicked">
      <div class="horizontalLine" />
      <div class="verticalLine" />
    </div>
  </div>
</template>

<style scoped>
.bissantzControls.sliderControl {
  --rangeSize: 22px;
  --lineThickness: 2px;
  --color: var(--slider-color);
  --marginButtonLine: 6px;

  --markerSize: 6px;
  --markerLength: calc(var(--markerSize) + 2 * var(--lineThickness));

  --thumbSize: 10px;
  --thumbLength: calc(var(--thumbSize) + 2 * var(--lineThickness));

  --buttonSize: var(--rangeSize);
  --buttonLineWidth: calc(var(--buttonSize) - (2 * var(--marginButtonLine)));
  --marginTrackLine: calc(var(--buttonLineWidth) - var(--marginButtonLine));

  opacity: 0.7;
  display: flex;
  height: var(--rangeSize);
  touch-action: none;

  .track {
    position: relative;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .horizontalLine {
      width: calc(100% - 2 * var(--marginTrackLine));
    }

    .trackItems {
      position: absolute;
      display: flex;
      align-items: center;
      width: calc(100% - 2 * var(--marginTrackLine) - var(--thumbLength));

      .marker-wrapper {
        display: flex;
        position: absolute;
        left: calc(var(--markerPosition) - var(--markerLength));
        justify-content: center;
        align-items: center;
        width: calc(var(--markerLength) * 2);
        height: calc(var(--markerLength) * 2);
        cursor: pointer;
      }

      .marker {
        width: var(--markerSize);
        height: var(--markerSize);
        border: var(--lineThickness) solid var(--color);
        border-radius: 50%;
        background-color: var(--color);
        -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
        cursor: pointer;
      }

      .thumb {
        position: absolute;
        left: calc(var(--thumbPosition) - var(--thumbLength) / 2);
        width: var(--thumbSize);
        height: var(--thumbSize);
        border: var(--lineThickness) solid var(--slider-thumb-color);
        border-radius: 50%;
        background-color: var(--slider-thumb-background);
        -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
        cursor: pointer;
      }
    }
  }

  .button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--buttonSize);
    width: var(--buttonSize);
    cursor: pointer;
  }

  .horizontalLine {
    width: calc(100% - 2 * var(--marginButtonLine));
    height: var(--lineThickness);
    background-color: var(--color);
  }

  .verticalLine {
    position: absolute;
    width: var(--lineThickness);
    height: calc(100% - 2 * var(--marginButtonLine));
    background-color: var(--color);
  }
}
</style>
